import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";

// Set axios base URL
const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL = configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
    ? ""
    : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

const DashboardContainer = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 2rem;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e9ecef;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    color: #333;
    margin: 0;
`;

const LogoutButton = styled.button`
    background: #dc3545;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1rem;
    transition: background 0.3s;

    &:hover {
        background: #c82333;
    }
`;

const MenuGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
`;

const MenuCard = styled.div`
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.3s, box-shadow 0.3s;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
    }
`;

const MenuIcon = styled.div`
    font-size: 3rem;
    margin-bottom: 1rem;
    text-align: center;
`;

const MenuTitle = styled.h2`
    font-size: 1.5rem;
    color: #333;
    margin: 0 0 1rem 0;
    text-align: center;
`;

const MenuDescription = styled.p`
    color: #666;
    text-align: center;
    margin: 0;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
`;

const StatCard = styled.div`
    background: white;
    padding: 1.5rem;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    text-align: center;
`;

const StatNumber = styled.div`
    font-size: 2rem;
    font-weight: bold;
    color: #667eea;
    margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
    color: #666;
    font-size: 0.9rem;
`;

const Modal = styled.div<{ isOpen: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: ${(props) => (props.isOpen ? "flex" : "none")};
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    padding: 2rem;
    border-radius: 10px;
    width: 90%;
    max-width: 800px;
    max-height: 80vh;
    overflow-y: auto;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    font-weight: bold;
    color: #333;
`;

const Input = styled.input`
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 1rem;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1rem;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1rem;
    transition: background 0.3s;

    ${(props) => {
        switch (props.variant) {
            case "primary":
                return `
                    background: #667eea;
                    color: white;
                    &:hover { background: #5a6fd8; }
                `;
            default:
                return `
                    background: #6c757d;
                    color: white;
                    &:hover { background: #5a6268; }
                `;
        }
    }}
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
`;

const Th = styled.th`
    background: #f8f9fa;
    padding: 1rem;
    text-align: left;
    border-bottom: 2px solid #dee2e6;
`;

const Td = styled.td`
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
`;

const LoginContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: #f8f9fa;
`;

const LoginForm = styled.form`
    background: white;
    padding: 3rem;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 400px;
`;

const LoginTitle = styled.h1`
    text-align: center;
    margin-bottom: 2rem;
    color: #333;
`;

const LoginFormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const LoginLabel = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    color: #333;
    font-weight: bold;
`;

const LoginInput = styled.input`
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 1rem;
`;

const LoginButton = styled.button`
    width: 100%;
    padding: 0.75rem;
    background: #667eea;
    color: white;
    border: none;
    border-radius: 5px;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
        background: #5a6fd8;
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

const ErrorMessage = styled.div`
    color: #dc3545;
    margin-bottom: 1rem;
    text-align: center;
`;

interface Stats {
    totalVideos: number;
    totalViews: number;
    activeUsers: number;
    serverUptime: string;
}

interface User {
    id: number;
    username: string;
    email: string;
    role: string;
    created_at: string;
}

interface Video {
    id: number;
    title: string;
    description: string;
    video_url: string;
    category: string;
    created_at: string;
}

const AdminDashboard: React.FC = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [stats, setStats] = useState<Stats>({
        totalVideos: 0,
        totalViews: 0,
        activeUsers: 0,
        serverUptime: "99.9%",
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState<"video" | "user" | "settings" | "stats">("video");
    const [users, setUsers] = useState<User[]>([]);
    const [videos, setVideos] = useState<Video[]>([]);

    useEffect(() => {
        const token = localStorage.getItem("adminToken");
        if (token) {
            setIsLoggedIn(true);
            fetchStats();
        }
    }, []);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setLoginError("");

        try {
            const response = await axios.post("/api/admin/login", {
                username,
                password,
            });

            if (response.data.success) {
                localStorage.setItem("adminToken", response.data.token);
                setIsLoggedIn(true);
                fetchStats();
            } else {
                setLoginError(response.data.error || "로그인에 실패했습니다.");
            }
        } catch (error) {
            setLoginError("로그인 중 오류가 발생했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("adminToken");
        setIsLoggedIn(false);
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("adminToken");
            const response = await axios.get("/api/admin/stats", {
                headers: { Authorization: `Bearer ${token}` },
            });
            const apiStats = response.data.data;
            setStats({
                totalVideos: (apiStats?.works || 0) + (apiStats?.hero_video || 0),
                totalViews: 0,
                activeUsers: apiStats?.contacts || 0,
                serverUptime: "99.9%"
            });
        } catch (error) {
            console.error("Error fetching stats:", error);
            // Set default values on error
            setStats({
                totalVideos: 0,
                totalViews: 0,
                activeUsers: 0,
                serverUptime: "99.9%"
            });
        }
    };

    const handleMenuClick = (type: "video" | "user" | "settings" | "stats") => {
        setModalType(type);
        setModalOpen(true);

        switch (type) {
            case "video":
                fetchVideos();
                break;
            case "user":
                fetchUsers();
                break;
            case "stats":
                fetchStats();
                break;
        }
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem("adminToken");
            const response = await axios.get("/api/admin/users", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(response.data.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const fetchVideos = async () => {
        try {
            const token = localStorage.getItem("adminToken");
            const response = await axios.get("/api/admin/videos", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setVideos(response.data.data);
        } catch (error) {
            console.error("Error fetching videos:", error);
        }
    };

    const renderModalContent = () => {
        switch (modalType) {
            case "video":
                return (
                    <div>
                        <h2>동영상 관리</h2>
                        <Button variant="primary" onClick={() => alert("동영상 업로드 기능을 구현하세요")}>
                            새 동영상 업로드
                        </Button>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>제목</Th>
                                    <Th>카테고리</Th>
                                    <Th>업로드 날짜</Th>
                                    <Th>작업</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {videos.map((video) => (
                                    <tr key={video.id}>
                                        <Td>{video.title}</Td>
                                        <Td>{video.category}</Td>
                                        <Td>{new Date(video.created_at).toLocaleDateString()}</Td>
                                        <Td>
                                            <Button onClick={() => alert("수정 기능을 구현하세요")}>수정</Button>
                                            <Button onClick={() => alert("삭제 기능을 구현하세요")}>삭제</Button>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                );
            case "user":
                return (
                    <div>
                        <h2>사용자 관리</h2>
                        <Button variant="primary" onClick={() => alert("사용자 추가 기능을 구현하세요")}>
                            새 사용자 추가
                        </Button>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>사용자명</Th>
                                    <Th>이메일</Th>
                                    <Th>역할</Th>
                                    <Th>가입일</Th>
                                    <Th>작업</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <Td>{user.username}</Td>
                                        <Td>{user.email}</Td>
                                        <Td>{user.role}</Td>
                                        <Td>{new Date(user.created_at).toLocaleDateString()}</Td>
                                        <Td>
                                            <Button onClick={() => alert("수정 기능을 구현하세요")}>수정</Button>
                                            <Button onClick={() => alert("삭제 기능을 구현하세요")}>삭제</Button>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                );
            case "settings":
                return (
                    <div>
                        <h2>시스템 설정</h2>
                        <Form>
                            <FormGroup>
                                <Label>사이트 제목</Label>
                                <Input type="text" defaultValue="Client Operation Server" />
                            </FormGroup>
                            <FormGroup>
                                <Label>관리자 이메일</Label>
                                <Input type="email" defaultValue="admin@example.com" />
                            </FormGroup>
                            <FormGroup>
                                <Label>YouTube API 키</Label>
                                <Input type="text" placeholder="YouTube API 키를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <Label>서버 포트</Label>
                                <Input type="number" defaultValue="5000" />
                            </FormGroup>
                            <ButtonGroup>
                                <Button onClick={() => setModalOpen(false)}>취소</Button>
                                <Button variant="primary" onClick={() => alert("설정이 저장되었습니다")}>
                                    저장
                                </Button>
                            </ButtonGroup>
                        </Form>
                    </div>
                );
            case "stats":
                return (
                    <div>
                        <h2>통계 보기</h2>
                        <StatsGrid>
                            <StatCard>
                                <StatNumber>{stats.totalVideos}</StatNumber>
                                <StatLabel>총 동영상 수</StatLabel>
                            </StatCard>
                            <StatCard>
                                <StatNumber>{stats.totalViews}</StatNumber>
                                <StatLabel>총 조회수</StatLabel>
                            </StatCard>
                            <StatCard>
                                <StatNumber>{stats.activeUsers}</StatNumber>
                                <StatLabel>활성 사용자</StatLabel>
                            </StatCard>
                            <StatCard>
                                <StatNumber>{stats.serverUptime}</StatNumber>
                                <StatLabel>서버 가동률</StatLabel>
                            </StatCard>
                        </StatsGrid>
                        <div style={{ marginTop: "2rem" }}>
                            <h3>최근 활동</h3>
                            <p>최근 7일간의 활동 통계를 여기에 표시합니다.</p>
                        </div>
                    </div>
                );
        }
    };

    if (!isLoggedIn) {
        return (
            <LoginContainer>
                <LoginForm onSubmit={handleLogin}>
                    <LoginTitle>관리자 로그인</LoginTitle>
                    {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
                    <LoginFormGroup>
                        <LoginLabel htmlFor="username">사용자명</LoginLabel>
                        <LoginInput id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required />
                    </LoginFormGroup>
                    <LoginFormGroup>
                        <LoginLabel htmlFor="password">비밀번호</LoginLabel>
                        <LoginInput id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    </LoginFormGroup>
                    <LoginButton type="submit" disabled={isLoading}>
                        {isLoading ? "로그인 중..." : "로그인"}
                    </LoginButton>
                </LoginForm>
            </LoginContainer>
        );
    }

    return (
        <DashboardContainer>
            <Header>
                <Title>관리자 대시보드</Title>
                <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
            </Header>

            <StatsGrid>
                <StatCard>
                    <StatNumber>{stats.totalVideos}</StatNumber>
                    <StatLabel>총 동영상 수</StatLabel>
                </StatCard>
                <StatCard>
                    <StatNumber>{stats.totalViews}</StatNumber>
                    <StatLabel>총 조회수</StatLabel>
                </StatCard>
                <StatCard>
                    <StatNumber>{stats.activeUsers}</StatNumber>
                    <StatLabel>활성 사용자</StatLabel>
                </StatCard>
                <StatCard>
                    <StatNumber>{stats.serverUptime}</StatNumber>
                    <StatLabel>서버 가동률</StatLabel>
                </StatCard>
            </StatsGrid>

            <MenuGrid>
                <MenuCard onClick={() => handleMenuClick("video")}>
                    <MenuIcon>🎬</MenuIcon>
                    <MenuTitle>동영상 업로드</MenuTitle>
                    <MenuDescription>동영상을 업로드하고 관리합니다</MenuDescription>
                </MenuCard>
                <MenuCard onClick={() => handleMenuClick("user")}>
                    <MenuIcon>👥</MenuIcon>
                    <MenuTitle>사용자 관리</MenuTitle>
                    <MenuDescription>사용자 계정을 관리합니다</MenuDescription>
                </MenuCard>
                <MenuCard onClick={() => handleMenuClick("settings")}>
                    <MenuIcon>⚙️</MenuIcon>
                    <MenuTitle>시스템 설정</MenuTitle>
                    <MenuDescription>시스템 설정을 변경합니다</MenuDescription>
                </MenuCard>
                <MenuCard onClick={() => handleMenuClick("stats")}>
                    <MenuIcon>📊</MenuIcon>
                    <MenuTitle>통계 보기</MenuTitle>
                    <MenuDescription>상세한 통계를 확인합니다</MenuDescription>
                </MenuCard>
            </MenuGrid>

            <Modal isOpen={modalOpen}>
                <ModalContent>
                    {renderModalContent()}
                    <ButtonGroup>
                        <Button onClick={() => setModalOpen(false)}>닫기</Button>
                    </ButtonGroup>
                </ModalContent>
            </Modal>
        </DashboardContainer>
    );
};

export default AdminDashboard;
